import React, { useEffect, useState } from "react";
import AdsNavbar from "../AdsNavbar";
import Calling from "../../../api/Calling";
import { toast } from "react-toastify";
import NotFound from "../../../components/ul/NotFound";
import generateCaptcha from "../../../components/captcha";
import {
  LuMegaphone,
  LuPlus,
  LuSquarePen,
  LuCheck,
  LuX,
  LuTrash2,
  LuSearch,
} from "react-icons/lu";

const InhouseAds = () => {
  const [loader, setLoader] = useState(false);
  const [ads, setAds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAd, setNewAd] = useState({ a_id: "", a_type: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAds, setSelectedAds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const adTypes = ["banner", "interstitial", "native", "video", "onlyimage"];

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getInhouseAds();
      if (resp?.status) {
        setAds(resp?.data);
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAds = ads.filter(
    (ad) =>
      ad.a_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.a_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handleSave = async (id) => {
    const adToUpdate = ads.find((ad) => ad._id === id);
    setLoader(true);
    try {
      const resp = await Calling.updateInhouseAds(id, {
        a_id: adToUpdate.a_id,
        a_type: adToUpdate.a_type,
      });
      if (resp?.status) {
        toast.success(resp?.message);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setEditingId(null);
      setLoader(false);
    }
  };

  const handleInputChange = (id, field, value) => {
    setAds(
      ads.map((item) => (item._id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddSubmit = async () => {
    if (newAd.a_id.trim() === "" || newAd.a_type.trim() === "") {
      toast.error("Please fill all fields");
      return;
    }

    setLoader(true);
    try {
      const body = {
        a_id: newAd.a_id.replace(/ /g, ""),
        a_type: newAd.a_type,
      };
      const resp = await Calling.addInhouseAds(body);
      if (resp?.status) {
        toast.success(resp?.message);
        setNewAd({ a_id: "", a_type: "" });
        setShowAddForm(false);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const handleBulkDelete = async (id = null) => {
    if (selectedAds.length === 0) return toast.error("No ads selected");

    const content = `Delete ${selectedAds.length} selected ads?`;
    const check = generateCaptcha(content);
    if (!check) return toast.error("Answer is not correct");

    setLoader(true);
    try {
      let body = {};
      if (id) {
        body = { id: [id] };
      } else {
        body = {
          id: selectedAds,
        };
      }
      const resp = await Calling.deleteInhouseAds(body);
      if (resp?.status) {
        toast.success(resp?.message);
        setSelectedAds([]);
        setSelectAll(false);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Server Error");
    } finally {
      setLoader(false);
    }
  };

  const handleSelectAd = (id) => {
    if (selectedAds.includes(id)) {
      setSelectedAds(selectedAds.filter((item) => item !== id));
    } else {
      setSelectedAds([...selectedAds, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAds([]);
    } else {
      setSelectedAds(filteredAds.map((item) => item._id));
    }
    setSelectAll(!selectAll);
  };

  return (
    <div className="text-main-font md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
          {/* Airy header */}
          <div className="flex items-center gap-4 pt-6 pb-5">
            <span className="p-3.5 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30">
              <LuMegaphone className="w-6 h-6" />
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-main-font">
                Ad Management
              </h1>
              <p className="text-sm text-main-subfont flex items-center gap-2">
                Manage your in-app ad units
                {loader && (
                  <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-brand-teal border-t-transparent animate-spin" />
                )}
              </p>
            </div>
          </div>

          <AdsNavbar selectState={"inhouseads"} />

          {/* Section toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-5 border-b border-main-border pb-5">
            <div className="flex items-baseline gap-2">
              <h2 className="text-lg font-bold text-main-font">Inhouse Ads</h2>
              <span className="text-sm text-main-subfont">
                ({filteredAds.length})
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative">
                <LuSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-main-subfont pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search Ads..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-main-border text-main-font placeholder-main-subfont outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                />
              </div>
              {selectedAds?.length > 0 && (
                <button
                  onClick={() => handleBulkDelete()}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm bg-red-500/90 hover:bg-red-500 text-white rounded-xl transition whitespace-nowrap"
                >
                  <LuTrash2 className="w-4 h-4" />
                  Delete Selected
                </button>
              )}
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-white bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl shadow-lg shadow-teal-500/30 hover:brightness-110 transition whitespace-nowrap"
              >
                <LuPlus className="w-4 h-4" />
                Add Inhouse Ad
              </button>
            </div>
          </div>

          {/* Add form */}
          {showAddForm && (
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            >
              <div
                className="ui-card-solid w-full max-w-2xl p-6 ui-rise max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-main-font">Add Inhouse Ad</h2>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-main-subfont hover:text-main-font hover:bg-main-bg transition-colors"
                  >
                    <LuX size={18} />
                  </button>
                </div>
                <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-main-subfont font-medium">
                    Ad ID
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Ad ID"
                    value={newAd.a_id}
                    onChange={(e) =>
                      setNewAd({ ...newAd, a_id: e.target.value })
                    }
                    className="ui-input w-full px-3 py-2.5 rounded-xl text-sm"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-main-subfont font-medium">
                    Ad Type
                  </label>
                  <select
                    value={newAd.a_type}
                    onChange={(e) =>
                      setNewAd({ ...newAd, a_type: e.target.value })
                    }
                    className="ui-input w-full px-3 py-2.5 rounded-xl text-sm"
                  >
                    <option value="">Select Type</option>
                    {adTypes.map((t, i) => (
                      <option key={i} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAddSubmit}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl shadow-lg shadow-teal-500/30 hover:brightness-110 transition"
                >
                  <LuCheck className="w-4 h-4" />
                  Submit
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-red-400/40 text-red-600 hover:bg-red-500/10 rounded-xl transition"
                >
                  <LuX className="w-4 h-4" />
                  Cancel
                </button>
              </div>
                </div>
              </div>
            </div>
          )}

          {/* Select-all control */}
          {filteredAds.length > 0 && (
            <label className="flex items-center gap-2 mt-5 text-sm text-main-subfont cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="accent-teal-500"
              />
              Select all
            </label>
          )}

          {/* Ads card grid */}
          {loader ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-white border border-main-border shadow-sm p-5 animate-pulse"
                >
                  <div className="h-6 w-24 bg-black/10 rounded mb-6" />
                  <div className="flex gap-2">
                    <div className="h-9 flex-1 bg-black/10 rounded-lg" />
                    <div className="h-9 flex-1 bg-black/10 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredAds.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
              {filteredAds.map((item, index) => (
                <div
                  key={index}
                  className={`group flex flex-col rounded-2xl bg-white shadow-sm border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 overflow-hidden ${
                    selectedAds.includes(item._id)
                      ? "border-brand-teal/50 ring-1 ring-brand-teal/30"
                      : "border-main-border hover:border-brand-teal/40"
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start gap-3 p-5 pb-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white flex items-center justify-center shadow-md shadow-teal-500/25 shrink-0">
                      <LuMegaphone className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-teal-500/15 text-brand-teal">
                        {item.a_type}
                      </span>
                      <p className="text-xs text-main-subfont mt-1">
                        Inhouse ad unit
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedAds.includes(item._id)}
                      onChange={() => handleSelectAd(item._id)}
                      className="accent-teal-500 mt-1 w-4 h-4 cursor-pointer shrink-0"
                    />
                  </div>

                  {/* Body */}
                  <div className="px-5 space-y-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-main-subfont mb-1">
                        Ad ID
                      </p>
                      {editingId === item._id ? (
                        <input
                          type="text"
                          value={item.a_id}
                          onChange={(e) =>
                            handleInputChange(item._id, "a_id", e.target.value)
                          }
                          className="ui-input p-2 w-full rounded-xl text-sm"
                        />
                      ) : (
                        <div className="rounded-lg bg-main-bg border border-main-border px-3 py-2 font-mono text-sm text-main-font break-all">
                          {item.a_id}
                        </div>
                      )}
                    </div>

                    {editingId === item._id && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-main-subfont mb-1">
                          Type
                        </p>
                        <select
                          value={item.a_type}
                          onChange={(e) =>
                            handleInputChange(item._id, "a_type", e.target.value)
                          }
                          className="ui-input p-2 w-full rounded-xl text-sm capitalize"
                        >
                          {adTypes.map((t, i) => (
                            <option key={i} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex gap-2 p-5 pt-4 mt-4 border-t border-main-border">
                    <button
                      onClick={() =>
                        editingId === item._id
                          ? handleSave(item._id)
                          : handleEdit(item._id)
                      }
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white rounded-lg transition-all ${
                        editingId === item._id
                          ? "bg-green-500 hover:bg-green-600"
                          : "bg-gradient-to-r from-teal-500 to-cyan-500 shadow-md shadow-teal-500/25 hover:brightness-110"
                      }`}
                    >
                      {editingId === item._id ? (
                        <LuCheck className="w-4 h-4" />
                      ) : (
                        <LuSquarePen className="w-4 h-4" />
                      )}
                      {editingId === item._id ? "Save" : "Edit"}
                    </button>

                    {editingId === item._id && (
                      <button
                        onClick={() => setEditingId(null)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-main-subfont bg-black/5 hover:bg-black/10 rounded-lg transition-colors"
                      >
                        <LuX className="w-4 h-4" />
                        Cancel
                      </button>
                    )}

                    <button
                      onClick={() => handleBulkDelete(item._id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold bg-red-500/90 hover:bg-red-500 text-white rounded-lg transition"
                    >
                      <LuTrash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-10 flex justify-center">
              <NotFound title="No ads yet" />
            </div>
          )}
    </div>
  );
};

export default InhouseAds;
